/**
 * lib/presetDiff.js — compare a user's effective permissions to the role preset.
 *
 * Pure functions only. No API calls, no React. Consumed by AdminUsers.jsx
 * (Preset column, filter, sort) and by AdminUsersPermissionsDialog.jsx
 * (summary card + preset-diff confirm dialog).
 *
 * Status semantics (product-approved):
 *   identical  → user permissions === preset (0 added, 0 removed)
 *   modified   → some overlap remains between user permissions and preset
 *                (i.e. at least one preset key is still granted)
 *   custom     → no overlap at all with the preset AND the user has ≥1 perm
 *   empty      → user has 0 permissions and preset is non-empty (treated as
 *                "custom" for UI purposes but exposed separately for tests)
 *
 * Super Admin has an implicit bypass and is not comparable against a preset;
 * callers should short-circuit before calling this helper.
 */

const toSet = (a) => new Set(Array.isArray(a) ? a : []);

/**
 * @param {string[]|Set<string>} effective  — user's effective permissions
 * @param {string[]|Set<string>} preset     — role preset permissions
 * @returns {{
 *   status: "identical" | "modified" | "custom" | "empty",
 *   added: string[],    // in effective, not in preset
 *   removed: string[],  // in preset, not in effective
 *   presetSize: number,
 *   currentSize: number,
 *   overlap: number,
 * }}
 */
export function computePresetDiff(effective, preset) {
  const E = toSet(effective);
  const P = toSet(preset);
  const added = [];
  const removed = [];
  E.forEach((k) => { if (!P.has(k)) added.push(k); });
  P.forEach((k) => { if (!E.has(k)) removed.push(k); });
  const overlap = [...E].filter((k) => P.has(k)).length;

  let status;
  if (added.length === 0 && removed.length === 0) status = "identical";
  else if (E.size === 0 && P.size > 0)             status = "empty";
  else if (overlap === 0)                          status = "custom";
  else                                             status = "modified";

  return {
    status,
    added: added.sort(),
    removed: removed.sort(),
    presetSize: P.size,
    currentSize: E.size,
    overlap,
  };
}

// Stable sort key so "Ordina per Preset" is deterministic:
// identical < modified < empty < custom.
const STATUS_RANK = { identical: 0, modified: 1, empty: 2, custom: 3 };
export function presetSortKey(diff) {
  if (!diff) return 99;
  return STATUS_RANK[diff.status] ?? 99;
}

// UI colour token per status — kept here so table + modal render consistently.
export const STATUS_STYLE = {
  identical: { chip: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Conforme" },
  modified:  { chip: "bg-amber-50 text-amber-900 border-amber-300",       label: "Modificato" },
  custom:    { chip: "bg-rose-50 text-rose-800 border-rose-300",          label: "Custom" },
  empty:     { chip: "bg-rose-50 text-rose-800 border-rose-300",          label: "Custom" },
};
